/**
 * Fila de escritas feitas sem conexão.
 *
 * O protótipo tratava o modo offline como um chip que muda de cor. Offline de
 * verdade é uma fila local, reenvio ao reconectar e uma resposta honesta para o
 * que o servidor recusar depois.
 *
 * ESCOPO, E POR QUE ELE É ESTE
 *
 * Só entram na fila as ações feitas em campo, longe de rede: abrir solicitação,
 * cadastrar contato e registrar visita. Ficam de fora aprovação de campanha,
 * administração e permissões — são decisões que dependem de ver o estado atual
 * do sistema, e executar às cegas para sincronizar depois é pior que esperar a
 * conexão voltar.
 *
 * O QUE ESTA FILA NÃO RESOLVE
 *
 * Edição concorrente do mesmo registro por dois aparelhos. Aqui só se
 * enfileiram CRIAÇÕES, que não competem entre si — duas pessoas criando
 * contatos diferentes offline não geram conflito. Fila de edição exigiria
 * resolução de conflito, que é outro problema e não está resolvido.
 */

const BANCO = 'mandato360-offline';
const LOJA = 'fila';
const VERSAO = 1;

export interface ItemDaFila {
  id: string;
  /** Chave de idempotência: gerada uma vez e reusada em toda tentativa. */
  chave: string;
  metodo: 'POST' | 'PUT';
  url: string;
  corpo: unknown;
  /** Descrição legível, para a tela dizer o que está pendente. */
  rotulo: string;
  criadoEm: number;
  tentativas: number;
  ultimoErro?: string;
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(BANCO, VERSAO);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LOJA)) {
        db.createObjectStore(LOJA, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function transacao<T>(
  modo: IDBTransactionMode,
  fn: (loja: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await abrir();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(LOJA, modo);
    const req = fn(tx.objectStore(LOJA));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export function gerarChave(): string {
  // crypto.randomUUID não existe em contexto inseguro nem em navegador antigo;
  // o alternativo não é criptográfico, mas a chave só precisa ser única neste
  // aparelho, e não imprevisível.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export async function enfileirar(
  item: Omit<ItemDaFila, 'id' | 'chave' | 'criadoEm' | 'tentativas'>,
): Promise<ItemDaFila> {
  const completo: ItemDaFila = {
    ...item,
    id: gerarChave(),
    chave: gerarChave(),
    criadoEm: Date.now(),
    tentativas: 0,
  };
  await transacao('readwrite', (loja) => loja.add(completo));
  notificar();
  return completo;
}

export async function listarFila(): Promise<ItemDaFila[]> {
  try {
    const itens = await transacao<ItemDaFila[]>('readonly', (loja) => loja.getAll());
    return itens.sort((a, b) => a.criadoEm - b.criadoEm);
  } catch {
    // Navegador com armazenamento bloqueado (aba privada, política do
    // dispositivo): a fila não existe, e dizer isso com uma lista vazia é
    // melhor que derrubar a tela.
    return [];
  }
}

export async function removerDaFila(id: string): Promise<void> {
  await transacao('readwrite', (loja) => loja.delete(id));
  notificar();
}

async function atualizarItem(item: ItemDaFila): Promise<void> {
  await transacao('readwrite', (loja) => loja.put(item));
  notificar();
}

// ---------------------------------------------------------------- assinantes

type Ouvinte = () => void;
const ouvintes = new Set<Ouvinte>();

export function assinarFila(fn: Ouvinte): () => void {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

function notificar(): void {
  ouvintes.forEach((fn) => fn());
}

// ------------------------------------------------------------------- reenvio

export interface ResultadoReenvio {
  enviados: number;
  falharam: number;
}

/**
 * Reenvia a fila, na ordem em que foi criada.
 *
 * A ordem importa: uma solicitação criada depois de um contato pode depender
 * dele. Reenviar em paralelo inverteria a sequência e a segunda falharia por
 * referência inexistente.
 *
 * Erro 4xx tira o item da fila e o reporta: pedido malformado ou rejeitado por
 * regra não melhora com repetição, e mantê-lo faria a fila tentar para sempre.
 * Erro de rede ou 5xx mantém o item para a próxima tentativa.
 */
export async function reenviarFila(
  apiUrl: string,
  token: string,
): Promise<ResultadoReenvio> {
  const itens = await listarFila();
  let enviados = 0;
  let falharam = 0;

  for (const item of itens) {
    try {
      const res = await fetch(`${apiUrl}${item.url}`, {
        method: item.metodo,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          // A mesma chave em toda tentativa: é ela que impede o servidor de
          // criar um segundo registro se a primeira chegou e a resposta se
          // perdeu.
          'Idempotency-Key': item.chave,
        },
        body: JSON.stringify(item.corpo),
      });

      if (res.ok) {
        await removerDaFila(item.id);
        enviados += 1;
        continue;
      }

      if (res.status >= 400 && res.status < 500) {
        let mensagem = `${res.status}`;
        try {
          const corpo = await res.json();
          const m = corpo?.message;
          mensagem = Array.isArray(m) ? m.join(' · ') : m || mensagem;
        } catch {
          // Corpo não-JSON: a mensagem fica sendo o status, que já diz algo.
        }
        await removerDaFila(item.id);
        falharam += 1;
        registrarRejeitado({ ...item, ultimoErro: mensagem });
        continue;
      }

      await atualizarItem({ ...item, tentativas: item.tentativas + 1, ultimoErro: `${res.status}` });
    } catch (e: any) {
      // Sem rede de novo: para o reenvio aqui. Continuar percorreria a fila
      // toda somando tentativas sem chance nenhuma de sucesso.
      await atualizarItem({
        ...item,
        tentativas: item.tentativas + 1,
        ultimoErro: e?.message ?? 'sem conexão',
      });
      break;
    }
  }

  return { enviados, falharam };
}

/**
 * Itens que o servidor recusou.
 *
 * Ficam em memória, não no IndexedDB: são para a pessoa que está com a tela
 * aberta agora resolver. Persisti-los criaria uma segunda fila, de coisas que
 * nunca vão sair sozinhas, e que ninguém lembraria de limpar.
 */
const rejeitados: ItemDaFila[] = [];

function registrarRejeitado(item: ItemDaFila): void {
  rejeitados.push(item);
  notificar();
}

export function listarRejeitados(): ItemDaFila[] {
  return [...rejeitados];
}

export function limparRejeitados(): void {
  rejeitados.length = 0;
  notificar();
}
